import {useState} from 'react';
import {ChevronDown} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@common-web/components/ui/dropdown-menu';
import {Label} from '@common-web/components/ui/label';
import {Textarea} from '@common-web/components/ui/textarea';
import {toast} from '@common-web/hooks/use-toast';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import type {PipelineStatusAction} from '@common/groupPipeline/types';
import type {GroupPipelineDetail} from '@internal/pages/group-pipeline/components/pipelineDisplay';
import {MoveToStepDialog} from '@internal/pages/group-pipeline/detail/MoveToStepDialog';

const LABELS: Record<PipelineStatusAction, string> = {
  hold: 'Put on hold',
  resume: 'Resume',
  notMovingForward: 'Mark not moving forward',
  reinstate: 'Reinstate',
};

const DONE: Record<PipelineStatusAction, string> = {
  hold: 'Put on hold',
  resume: 'Resumed',
  notMovingForward: 'Marked not moving forward',
  reinstate: 'Reinstated',
};

const REASON_COPY: Partial<
  Record<PipelineStatusAction, {description: string; placeholder: string}>
> = {
  hold: {
    description: 'Task actions pause. Resuming pushes open due dates out by the days on hold.',
    placeholder: 'Employer paused until their Q4 budget is approved',
  },
  notMovingForward: {
    description: 'Task actions stop. Reinstating restarts the open steps with fresh due dates.',
    placeholder: 'Went with another TPA',
  },
};

// Which status changes apply to the group's current state.
function availableActions(pipeline: GroupPipelineDetail): PipelineStatusAction[] {
  if (pipeline.offState === 'ON_HOLD') return ['resume', 'notMovingForward'];
  if (pipeline.offState === 'NOT_MOVING_FORWARD') return ['reinstate'];
  return ['hold', 'notMovingForward'];
}

export function StatusMenu({
  vbaGroupId,
  pipeline,
}: {
  vbaGroupId: string;
  pipeline: GroupPipelineDetail;
}) {
  const utils = trpc.useUtils();
  const [reasonFor, setReasonFor] = useState<PipelineStatusAction | null>(null);
  const [reason, setReason] = useState('');
  const [moveOpen, setMoveOpen] = useState(false);

  const setStatus = trpc.internal.groupPipeline.setStatus.useMutation({
    onSuccess: (_result, {action}) => {
      setReasonFor(null);
      toast({title: DONE[action]});
      return Promise.all([
        utils.internal.groupPipeline.detail.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.activity.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.list.invalidate(),
        utils.internal.groupPipeline.board.invalidate(),
        utils.internal.groupPipeline.boardColumn.invalidate(),
      ]);
    },
    onError: createMutationErrorHandler('Could not change the status'),
  });

  const choose = (action: PipelineStatusAction) => {
    if (REASON_COPY[action]) {
      setReason('');
      setReasonFor(action);
    } else {
      setStatus.mutate({vbaGroupId, action});
    }
  };

  const copy = reasonFor ? REASON_COPY[reasonFor] : undefined;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='outline' className='bg-white' disabled={setStatus.isPending}>
            Status
            <ChevronDown className='ml-1.5 h-4 w-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' className='w-56'>
          {availableActions(pipeline).map((action) => (
            <DropdownMenuItem key={action} onSelect={() => choose(action)}>
              {LABELS[action]}
            </DropdownMenuItem>
          ))}
          {!pipeline.offState && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setMoveOpen(true)}>Move to step</DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <MoveToStepDialog
        vbaGroupId={vbaGroupId}
        pipeline={pipeline}
        open={moveOpen}
        onOpenChange={setMoveOpen}
      />

      <Dialog open={Boolean(reasonFor)} onOpenChange={(open) => !open && setReasonFor(null)}>
        {reasonFor && copy && (
          <DialogContent className='sm:max-w-md'>
            <DialogHeader>
              <DialogTitle>{LABELS[reasonFor]}</DialogTitle>
              <DialogDescription>{copy.description}</DialogDescription>
            </DialogHeader>
            <div className='space-y-2'>
              <Label htmlFor='status-reason'>Reason</Label>
              <Textarea
                className='bg-white'
                id='status-reason'
                autoFocus
                rows={3}
                value={reason}
                placeholder={copy.placeholder}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={() => setReasonFor(null)}>
                Cancel
              </Button>
              <Button
                disabled={!reason.trim() || setStatus.isPending}
                onClick={() =>
                  setStatus.mutate({vbaGroupId, action: reasonFor, reason: reason.trim()})
                }
              >
                {LABELS[reasonFor]}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}
